import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import useCheckoutPayment from 'hooks/useCheckoutPayment'
import { currencyFormat } from 'utils/format'
import { CHECKOUT_PAYMENT_CONFIRMATION } from 'constants/checkout-steps'
import { ORDERS } from 'constants/app-paths'

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import { InfoPage } from 'templates/InfoPage'
import { Card } from 'ui-components/Card'
import { Alert } from 'ui-components/Alert'
import { Button } from 'ui-components/Button'
import DescItemInfo from 'ui-components/DescItemInfo/DescItemInfo'
import { ApiErrors } from 'ui-components/ApiErrors'

import { PrintPaymentButton } from 'components/PrintPaymentButton'
import { SideMenu } from 'components/Checkout/SideMenu'
import { RequestButton } from 'components/RequestButton'

import { PaymentItems } from './components/PaymentItems'
import { PaymentPlans } from './components/PaymentPlans'
import { Total, Bottom, AlertButton } from './styles'

export default function ConfirmPayment() {
  const [responseError, setResponseError] = useState()
  const [saved, setSaved] = useState(false)
  const [orderPayment, setOrderPayment] = useState()

  const history = useHistory()

  const [
    {
      customerId,
      customerName,
      paymentItems,
      paymentVouchers,
      to_generate_debt: toGenerateDebt,
      to_generate_voucher: toGenerateVoucher,
      change_value: changeValue,
      totalPaid,
      totalToPay,
      finishingPayment,
    },
    {
      balanceValue,
      negativePayment,
      positivePayment,
      setDebtRegistration,
      setVoucherRegistration,
      setChangeValue,
      resetChangeValue,
      finishPayment,
      hasCustomer,
      validPaymentPlans,
    },
  ] = useCheckoutPayment()

  useEffect(() => {
    if (!hasCustomer()) history.push(ORDERS)
  }, [hasCustomer, history])

  const onClickFinish = async () => {
    setResponseError()
    const response = await finishPayment()

    if (response.error) return setResponseError(response)

    setOrderPayment(response.data)
    return setSaved(true)
  }

  const disableFinishButton = () => (
    (negativePayment() && !toGenerateDebt)
    || (positivePayment() && !toGenerateVoucher && !changeValue)
  )

  const renderSavedMessage = () => {
    if (!saved) return null

    return (
      <Alert severity="success">
        Pagamento registrado com sucesso!
      </Alert>
    )
  }

  const renderGenerateDebt = () => {
    if (!negativePayment() || toGenerateDebt) return null

    const title = 'Valor pago menor que o valor a pagar'

    if (customerId) {
      return (
        <Alert severity="error" title={title}>
          Deseja gerar um débito para o cliente?

          <AlertButton
            disabled={saved}
            size="small"
            color="secondary"
            onClick={() => setDebtRegistration(true)}
          >
            Gerar débito
          </AlertButton>
        </Alert>
      )
    }

    return (
      <Alert severity="error" title={title}>
        <p>
          Cliente não possui cadastro.
          Não será possível prosseguir com o pagamento.
        </p>
      </Alert>
    )
  }

  const renderGenerateVoucher = () => {
    if (!positivePayment() || toGenerateVoucher) return null
    if (balanceValue() === changeValue) return null

    const title = 'Valor pago é maior que o valor a pagar'

    if (customerId) {
      return (
        <Alert severity="warning" title={title}>
          <p>
            Deseja deixar crédito gerando um voucher para o cliente, ou irá devolver troco?
          </p>

          <AlertButton
            disabled={saved}
            size="small"
            onClick={() => setChangeValue()}
          >
            Devolver troco
          </AlertButton>

          <AlertButton
            disabled={saved}
            size="small"
            color="primary"
            onClick={() => setVoucherRegistration(true)}
          >
            Gerar voucher
          </AlertButton>
        </Alert>
      )
    }

    return (
      <Alert severity="warning" title={title}>
        <p>
          Registar o troco para o cliente?
        </p>

        <AlertButton
          disabled={saved}
          size="small"
          onClick={() => setChangeValue()}
        >
          Devolver troco
        </AlertButton>
      </Alert>
    )
  }

  const renderDebtRegistration = () => (
    <Alert title="Registro de débito">
      Será gerado um débito no valor do saldo negativo.

      <AlertButton
        disabled={saved}
        size="small"
        onClick={() => setDebtRegistration(false)}
      >
        Cancelar registro de débito
      </AlertButton>
    </Alert>
  )

  const renderVoucherRegistration = () => (
    <Alert title="Registro de crédito">
      Será gerado um crédito no valor do saldo restante.

      <AlertButton
        disabled={saved}
        size="small"
        onClick={() => setVoucherRegistration(false)}
      >
        Cancelar registro de crédito
      </AlertButton>
    </Alert>
  )

  const renderChangeValueRegistration = () => (
    <Alert title="Registro com troco">
      Pagamento será registrado com troco.

      <AlertButton
        disabled={saved}
        size="small"
        onClick={() => resetChangeValue()}
      >
        Cancelar troco
      </AlertButton>
    </Alert>
  )

  return (
    <InfoPage
      title="Pagamento"
      subtitle="Resumo do pagamento"
      renderMenu={() => <SideMenu step={CHECKOUT_PAYMENT_CONFIRMATION} />}
    >
      <Card>
        <ApiErrors responseError={responseError} />

        {renderSavedMessage()}

        <DescItemInfo label="Cliente">
          {customerName}
        </DescItemInfo>

        <PaymentItems
          paymentItems={paymentItems}
        />

        <PaymentPlans
          paymentPlans={validPaymentPlans()}
          paymentVouchers={paymentVouchers}
        />

        <h4>TOTAL</h4>
        <Total>
          <DescItemInfo label="R$ A pagar">
            {currencyFormat(totalToPay)}
          </DescItemInfo>

          <DescItemInfo label="R$ Pago">
            {currencyFormat(totalPaid)}
          </DescItemInfo>

          <DescItemInfo label="R$ Saldo">
            {currencyFormat(balanceValue())}
          </DescItemInfo>
        </Total>

      </Card>

      <br />
      { toGenerateDebt ? renderDebtRegistration() : null }
      { renderGenerateDebt() }
      { renderGenerateVoucher() }
      { toGenerateVoucher ? renderVoucherRegistration() : null }
      { changeValue && changeValue > 0 ? renderChangeValueRegistration() : null }
      <br />

      { saved ? (
        <PrintPaymentButton paymentId={orderPayment.id} />
      ) : (
        <Bottom>
          <Button
            disabled={finishingPayment}
            onClick={() => history.goBack()}
          >
            <ChevronLeftIcon />
            Voltar
          </Button>

          <RequestButton
            color="primary"
            disabled={disableFinishButton()}
            onClick={onClickFinish}
            text="Confirmar pagamento"
            loading={finishingPayment}
          />
        </Bottom>
      ) }
    </InfoPage>
  )
}
